import {
  AttachFile,
  Comment,
  Event,
  Quiz,
  Download,
  Email,
  Send,
  EmailOutlined,
  ExpandMore,
  ExpandLess,
  Phone,
  PhoneForwarded,
  Sms,
  Notifications,
  NotificationsActive,
  MobileScreenShare,
  PhoneAndroid,
  PictureAsPdf,
  Forward,
  ReportGmailerrorred,
  Campaign,
  MarkunreadMailbox,
  TrendingUp,
  PersonAdd,
  StickyNote2,
  Lightbulb,
  Star,
  Settings,
  Autorenew,
  DraftsOutlined,
  Replay,
  Cancel,
  LocationOn,
  LocalOffer,
  Login,
  ShoppingCart,
  Draw,
  Payments,
  AccountBalance,
  Receipt,
  Warning,
  VerifiedUser,
  Gavel,
  EvStation,
  ElectricalServices,
  Air,
  Bolt,
  Lock,
  Apartment,
  Work,
  Error,
  Description,
  Edit,
  Check,
  Close,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { toast } from "sonner";
import {
  ProcessedEventData,
  isCommentEventData,
  isFileAttachmentEventData,
  isSurveyEventData,
  isEmailEventData,
} from "../../types/EventData.types";
import {
  EventDataTypeEnum,
  useDownloadEventDataFileMutation,
  useUpdateWorkCommentMutation,
} from "../../store/services/timelineApiRTK";

interface EventDataItemProps {
  event: ProcessedEventData;
  isLast?: boolean;
  workId?: number;
}

const EventDataItem = ({ event, isLast, workId }: EventDataItemProps) => {
  const [emailExpanded, setEmailExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const [downloadFile, { isLoading: downloading }] = useDownloadEventDataFileMutation();
  const [updateComment, { isLoading: savingComment }] = useUpdateWorkCommentMutation();

  const isComment =
    event.Type === EventDataTypeEnum.Text &&
    !!event.parsedData &&
    isCommentEventData(event.parsedData);

  const startEditing = () => {
    if (event.parsedData && isCommentEventData(event.parsedData)) {
      setEditValue(event.parsedData.Description);
      setIsEditing(true);
    }
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditValue("");
  };

  const saveEditing = async () => {
    const trimmed = editValue.trim();
    if (!trimmed) return;

    try {
      await updateComment({
        eventDataId: event.Id,
        text: trimmed,
        workId,
      }).unwrap();
      toast.success("Anteckning uppdaterad");
      setIsEditing(false);
      setEditValue("");
    } catch {
      toast.error("Det gick inte att uppdatera anteckningen.");
    }
  };
  
  const formatDateShort = (dateString: string) => {
    return format(new Date(dateString), "d MMM", { locale: sv });
  };

  const iconMap: Record<string, React.ReactElement> = {
    Comment: <Comment fontSize="small" />,
    AttachFile: <AttachFile fontSize="small" />,
    Quiz: <Quiz fontSize="small" />,
    Email: <Email fontSize="small" />,
    Send: <Send fontSize="small" />,
    EmailOutlined: <EmailOutlined fontSize="small" />,
    Phone: <Phone fontSize="small" />,
    PhoneForwarded: <PhoneForwarded fontSize="small" />,
    Sms: <Sms fontSize="small" />,
    Notifications: <Notifications fontSize="small" />,
    NotificationsActive: <NotificationsActive fontSize="small" />,
    MobileScreenShare: <MobileScreenShare fontSize="small" />,
    PhoneAndroid: <PhoneAndroid fontSize="small" />,
    PictureAsPdf: <PictureAsPdf fontSize="small" />,
    Forward: <Forward fontSize="small" />,
    ReportGmailerrorred: <ReportGmailerrorred fontSize="small" />,
    Campaign: <Campaign fontSize="small" />,
    MarkunreadMailbox: <MarkunreadMailbox fontSize="small" />,
    TrendingUp: <TrendingUp fontSize="small" />,
    PersonAdd: <PersonAdd fontSize="small" />,
    StickyNote2: <StickyNote2 fontSize="small" />,
    Lightbulb: <Lightbulb fontSize="small" />,
    Star: <Star fontSize="small" />,
    Settings: <Settings fontSize="small" />,
    Autorenew: <Autorenew fontSize="small" />,
    DraftsOutlined: <DraftsOutlined fontSize="small" />,
    Replay: <Replay fontSize="small" />,
    Cancel: <Cancel fontSize="small" />,
    LocationOn: <LocationOn fontSize="small" />,
    LocalOffer: <LocalOffer fontSize="small" />,
    Login: <Login fontSize="small" />,
    ShoppingCart: <ShoppingCart fontSize="small" />,
    Draw: <Draw fontSize="small" />,
    Payments: <Payments fontSize="small" />,
    AccountBalance: <AccountBalance fontSize="small" />,
    Receipt: <Receipt fontSize="small" />,
    Warning: <Warning fontSize="small" />,
    VerifiedUser: <VerifiedUser fontSize="small" />,
    Gavel: <Gavel fontSize="small" />,
    EvStation: <EvStation fontSize="small" />,
    ElectricalServices: <ElectricalServices fontSize="small" />,
    Air: <Air fontSize="small" />,
    Bolt: <Bolt fontSize="small" />,
    Lock: <Lock fontSize="small" />,
    Apartment: <Apartment fontSize="small" />,
    Work: <Work fontSize="small" />,
    Error: <Error fontSize="small" />,
    Description: <Description fontSize="small" />,
    Event: <Event fontSize="small" />,
  };

  const getEventIcon = () => {
    return iconMap[event.icon] || <Event fontSize="small" />;
  };

  const getEventIconColor = () => {
    return event.color || "#757575";
  };

  const renderEventContent = () => {
    if (!event.parsedData) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
          Ingen data tillgänglig
        </Typography>
      );
    }

    switch (event.Type) {
      case EventDataTypeEnum.Text:
        if (isCommentEventData(event.parsedData)) {
          if (isEditing) {
            return (
              <Box sx={{ ml: 4 }}>
                <TextField
                  autoFocus
                  fullWidth
                  multiline
                  minRows={3}
                  variant="outlined"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  disabled={savingComment}
                  sx={{
                    "& .MuiOutlinedInput-root": { borderRadius: 2 },
                  }}
                />
                <Box display="flex" gap={1} mt={1}>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={
                      savingComment ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <Check />
                      )
                    }
                    disabled={savingComment || !editValue.trim()}
                    onClick={saveEditing}
                    sx={{ borderRadius: 2, fontSize: "0.75rem" }}
                  >
                    Spara
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Close />}
                    disabled={savingComment}
                    onClick={cancelEditing}
                    sx={{ borderRadius: 2, fontSize: "0.75rem" }}
                  >
                    Avbryt
                  </Button>
                </Box>
              </Box>
            );
          }
          return (
            <Typography variant="body2" sx={{ ml: 4, whiteSpace: "pre-wrap" }}>
              {event.parsedData.Description}
            </Typography>
          );
        }
        break;

      case EventDataTypeEnum.FileCrmDocument:
        if (event.parsedData && isFileAttachmentEventData(event.parsedData)) {
          const fileData = event.parsedData;
          return (
            <Box sx={{ ml: 4 }}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Typography variant="body2" fontWeight="500">
                  {fileData.OrginalFilename}
                </Typography>
                <Chip
                  label="Fil"
                  size="small"
                  color="success"
                  variant="outlined"
                />
              </Box>
              <Button
                size="small"
                startIcon={downloading ? <CircularProgress size={16} /> : <Download />}
                variant="outlined"
                disabled={downloading}
                onClick={async () => {
                  try {
                    await downloadFile({
                      eventDataId: event.Id,
                      filename: fileData.OrginalFilename,
                    }).unwrap();
                  } catch {
                    toast.error("Det gick inte att ladda ner filen.");
                  }
                }}
                sx={{ fontSize: "0.75rem" }}
              >
                Ladda ner
              </Button>
            </Box>
          );
        }
        break;

      case EventDataTypeEnum.SurveyResponse:
        if (isSurveyEventData(event.parsedData)) {
          return (
            <Box sx={{ ml: 4 }}>
              <Typography variant="body2" gutterBottom>
                Enkät instans: {event.parsedData.InstanceId}
              </Typography>
              {event.parsedData.SurveyId && (
                <Typography variant="caption" color="text.secondary">
                  Survey ID: {event.parsedData.SurveyId}
                </Typography>
              )}
            </Box>
          );
        }
        break;

      case EventDataTypeEnum.IncomingEmail:
      case EventDataTypeEnum.OutgoingEmail:
      case EventDataTypeEnum.UnprocessedEmail:
        if (isEmailEventData(event.parsedData)) {
          return (
            <Box sx={{ ml: 4 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Box flex={1}>
                  <Typography variant="body2" fontWeight="500" gutterBottom>
                    {event.parsedData.Subject}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Från: {event.parsedData.From} → Till: {event.parsedData.To}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  endIcon={emailExpanded ? <ExpandLess /> : <ExpandMore />}
                  onClick={() => setEmailExpanded(!emailExpanded)}
                  sx={{ ml: 1, fontSize: "0.75rem" }}
                >
                  {emailExpanded ? "Dölj" : "Visa"}
                </Button>
              </Box>
              
              <Collapse in={emailExpanded}>
                <Box sx={{ 
                  bgcolor: "grey.50", 
                  p: 2, 
                  borderRadius: 1, 
                  border: "1px solid",
                  borderColor: "grey.200",
                  mt: 1 
                }}>
                  {event.parsedData.Text && (
                    <Box mb={2}>
                      <Typography variant="caption" color="text.secondary" fontWeight="500">
                        Innehåll:
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          whiteSpace: "pre-wrap",
                          maxHeight: "200px",
                          overflow: "auto",
                          mt: 0.5
                        }}
                      >
                        {event.parsedData.Text}
                      </Typography>
                    </Box>
                  )}
                  
                  {event.parsedData.SpamScore && (
                    <Box mb={1}>
                      <Typography variant="caption" color="text.secondary">
                        Spam-poäng: <strong>{event.parsedData.SpamScore}</strong>
                      </Typography>
                    </Box>
                  )}
                  
                  {event.parsedData.Attachments?.length > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Bilagor: {event.parsedData.Attachments.length} st
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Collapse>
            </Box>
          );
        }
        break;

      default: {
        // Render parsed data as a compact key/value list
        const data = event.parsedData as unknown as Record<string, unknown> | null;
        if (!data || typeof data !== "object") {
          return null;
        }
        const entries = Object.entries(data).filter(
          ([, v]) => v !== null && v !== undefined && v !== ""
        );
        if (entries.length === 0) return null;
        return (
          <Box sx={{ ml: 4, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {entries.slice(0, 6).map(([key, value]) => (
              <Chip
                key={key}
                label={`${key}: ${typeof value === "object" ? "..." : String(value).substring(0, 40)}`}
                size="small"
                variant="outlined"
                sx={{ fontSize: "0.7rem", maxWidth: 280 }}
              />
            ))}
            {entries.length > 6 && (
              <Chip
                label={`+${entries.length - 6} fält`}
                size="small"
                sx={{ fontSize: "0.7rem" }}
              />
            )}
          </Box>
        );
      }
    }

    return (
      <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
        Data kunde inte tolkas
      </Typography>
    );
  };

  return (
    <ListItem
      divider={!isLast}
      sx={{ 
        px: 0,
        "&:hover": {
          bgcolor: "action.hover",
          borderRadius: 1,
        },
        transition: "background-color 0.2s ease",
      }}
    >
      <ListItemText
        primary={
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            mb={1}
          >
            <Avatar
              sx={{
                bgcolor: getEventIconColor(),
                width: 32,
                height: 32,
              }}
            >
              {getEventIcon()}
            </Avatar>
            <Box flex={1}>
              <Typography variant="body1" fontWeight="600">
                {event.displayName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatDateShort(event.Time)} av {event.CreatedBy.Name}
              </Typography>
            </Box>
            {isComment && !isEditing && (
              <Tooltip title="Redigera anteckning">
                <IconButton
                  size="small"
                  onClick={startEditing}
                  aria-label="Redigera anteckning"
                >
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        }
        secondary={
          <Stack spacing={1}>
            {renderEventContent()}
          </Stack>
        }
        secondaryTypographyProps={{ component: "div" }}
      />
    </ListItem>
  );
};

export default EventDataItem;